import type { ReactNode, RefObject } from "react";
import OnlineTestHeader from "./OnlineTestHeader";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";

interface OnlineTestLayoutProps {
    children: ReactNode;

    showHeader?: boolean;
    headerTimeRemaining?: string;
    isTimeRunningOut?: boolean;
    shouldBounceTimer?: boolean;
    onBounceEnd?: () => void;
    headerStudentName?: string;
    /** Second header row, on the header's own grid. Holds the student name on mobile, and stays put while content scrolls. */
    subHeader?: ReactNode;
    onOverviewClick?: () => void;
    onClose?: () => void;

    topBar?: ReactNode;
    footer?: ReactNode;
    footerScrolls?: boolean;
    scrollRef?: RefObject<HTMLDivElement>;
}

const OnlineTestLayout = (props: OnlineTestLayoutProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const headerRowClass = `max-w-[1000px] mx-auto w-full ${getStyleBasedOnBreakpoint(Breakpoint.MD, "px-m", "px-s")}`;

    const headerBottomSpacing = props.subHeader ? "pb-s" : "pb-l";

    return (
        <div className="h-full flex flex-col w-full">
            {props.showHeader && (
                <div
                    className={`z-50 w-full bg-background-light ${headerBottomSpacing} ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "pt-xl",
                        "pt-m"
                    )}`}
                >
                    <OnlineTestHeader
                        timeRemaining={props.headerTimeRemaining}
                        isTimeRunningOut={props.isTimeRunningOut}
                        shouldBounceTimer={props.shouldBounceTimer}
                        onBounceEnd={props.onBounceEnd}
                        studentName={props.headerStudentName}
                        onOverviewClick={props.onOverviewClick}
                        onClose={props.onClose}
                    />
                    {props.subHeader && <div className={`${headerRowClass} pt-l`}>{props.subHeader}</div>}
                </div>
            )}

            {props.footerScrolls ? (
                <div
                    ref={props.scrollRef}
                    className="overflow-y-auto flex-1 min-h-0 flex flex-col"
                >
                    {props.topBar && <div className="w-full px-l pt-m shrink-0">{props.topBar}</div>}
                    <div className="flex flex-col items-center w-full max-w-[1000px] mx-auto px-s grow shrink-0">
                        {props.children}
                    </div>
                    {props.footer}
                </div>
            ) : (
                <>
                    {props.topBar && <div className="w-full px-l pt-m shrink-0">{props.topBar}</div>}
                    <div
                        ref={props.scrollRef}
                        className="flex flex-col items-center w-full max-w-[1000px] mx-auto px-s overflow-y-auto flex-1 min-h-0"
                    >
                        {props.children}
                    </div>
                    {props.footer}
                </>
            )}
        </div>
    );
};

export default OnlineTestLayout;
